import React, { KeyboardEvent, useState } from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import LoadingButton from "@atlaskit/button/loading-button";
import { FormFooter } from "@atlaskit/form";
import { ErrorMessage } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Flex, Stack, Box, xcss, media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { PageWorkTypesLeftProps } from "../../../common/types";
import { useIsTeamTypeSpaceRecommendationExperiment } from "../../../hooks/experiment-hooks";
import { IssueTypeIcon } from "../../assets/issue-type-svgs";

import messages from "./messages";

export const LeftHandSidePageWorkTypes: React.FC<PageWorkTypesLeftProps> = ({
    defaultIssueTypes,
    selectedIssueTypes,
    onToggleIssueType,
    onClickNext
}) => {
    const { formatMessage } = useIntl();
    const [showError, setShowError] = useState(false);
    const isTeamTypeSpaceRecommendationExperiment: boolean =
        useIsTeamTypeSpaceRecommendationExperiment();
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );

    const handleNextClick = () => {
        if (selectedIssueTypes.length === 0) {
            setShowError(true);
        } else {
            setShowError(false);
            onClickNext();
        }
    };

    return (
        <Flex
            direction="column"
            justifyContent="space-between"
            xcss={containerStyles}
        >
            <Stack space="space.150">
                <div css={[modalVisualUpdates && titleVisualUpdateCss]}>
                    <Heading as="h1" size="xlarge">
                        <FormattedMessage {...messages.chooseWorkTypes} />
                    </Heading>
                </div>
                {modalVisualUpdates &&
                !isTeamTypeSpaceRecommendationExperiment ? (
                    <p css={subtextVisualUpdateStyles}>
                        <FormattedMessage
                            {...messages.workTypesDescriptionVisualUpdate}
                        />
                    </p>
                ) : (
                    <Box xcss={subtextStyles}>
                        <FormattedMessage
                            {...(!isTeamTypeSpaceRecommendationExperiment
                                ? messages.workTypesDescription
                                : messages.workTypesDescriptionVariation)}
                        />
                    </Box>
                )}
                <Box xcss={scrollableContainerStyles} role="list">
                    <Stack space="space.050">
                        {defaultIssueTypes.map((option) => (
                            <Box
                                xcss={[
                                    pressStyles,
                                    modalVisualUpdates &&
                                        pressVisualUpdateStyles,
                                    selectedIssueTypes.includes(option)
                                        ? activePressStyles
                                        : undefined
                                ]}
                                testId={option.id}
                                key={option.id}
                                onClick={() => {
                                    onToggleIssueType(option);
                                    setShowError(false);
                                }}
                                onKeyDown={(e: KeyboardEvent) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onToggleIssueType(option);
                                        setShowError(false);
                                    }
                                }}
                                role="listitem"
                                tabIndex={0}
                            >
                                <Flex
                                    alignItems="center"
                                    columnGap={
                                        modalVisualUpdates
                                            ? "space.150"
                                            : "space.300"
                                    }
                                >
                                    <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                        xcss={
                                            !modalVisualUpdates
                                                ? iconStyles
                                                : undefined
                                        }
                                    >
                                        <IssueTypeIcon type={option.icon} />
                                    </Flex>
                                    <Flex direction="column" alignItems="start">
                                        {modalVisualUpdates ? (
                                            <span
                                                id={`option-name-${option.name}`}
                                                css={
                                                    tileTitleVisualUpdateStyles
                                                }
                                            >
                                                {formatMessage(option.name)}
                                            </span>
                                        ) : (
                                            <Box
                                                as="span"
                                                id={`option-name-${option.name}`}
                                            >
                                                {formatMessage(option.name)}
                                            </Box>
                                        )}
                                        {modalVisualUpdates ? (
                                            <span
                                                id={`option-description-${option.name}`}
                                                css={
                                                    tileDescriptionVisualUpdateStyles
                                                }
                                            >
                                                {formatMessage(
                                                    option.description
                                                )}
                                            </span>
                                        ) : (
                                            <Box
                                                xcss={descriptionStyles}
                                                id={`option-description-${option.name}`}
                                            >
                                                {formatMessage(
                                                    option.description
                                                )}
                                            </Box>
                                        )}
                                    </Flex>
                                </Flex>
                            </Box>
                        ))}
                    </Stack>
                </Box>
                {showError && (
                    <ErrorMessage>
                        <FormattedMessage {...messages.issueTypeValidation} />
                    </ErrorMessage>
                )}
                {modalVisualUpdates ? (
                    <p css={addMoreLaterVisualUpdateStyles}>
                        <FormattedMessage {...messages.addMoreWorkTypesLater} />
                    </p>
                ) : (
                    <Box as="p" xcss={subtextStyles}>
                        <FormattedMessage {...messages.addMoreWorkTypesLater} />
                    </Box>
                )}
            </Stack>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <div
                        css={[
                            modalVisualUpdates && submitButtonVisualUpdateCss
                        ]}
                    >
                        <LoadingButton
                            type="submit"
                            appearance="primary"
                            isLoading={false}
                            onClick={handleNextClick}
                            aria-labelledby="next-button step-label"
                        >
                            <FormattedMessage {...messages.nextButtonCta} />
                        </LoadingButton>
                    </div>
                </Flex>
            </FormFooter>
        </Flex>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const scrollableContainerStyles = xcss({
    [media.above.sm]: {
        maxHeight: "360px",
        overflowY: "auto"
    }
});

const pressStyles = xcss({
    "border": "2px solid",
    "borderRadius": "border.radius.200",
    "borderColor": "color.border",
    "backgroundColor": "elevation.surface",
    "maxWidth": "420px",
    "minHeight": "64px",
    "padding": "space.050",
    ":hover": {
        borderColor: "color.border.focused"
    }
});

const subtextStyles = xcss({
    fontSize: "font.body.small",
    color: "color.text.subtle",
    margin: "space.0"
});

const activePressStyles = xcss({
    borderColor: "color.border.focused",
    borderWidth: "border.width.indicator"
});

const iconStyles = xcss({
    paddingInlineStart: "space.150"
});

const descriptionStyles = xcss({
    fontSize: "font.body.small",
    color: "color.text.subtle"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});

// Visual update styles for cfind4831ProductOverlayModalVisualUpdates gate
const submitButtonVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const addMoreLaterVisualUpdateStyles = css({
    font: token("font.body.small"),
    color: token("color.text.subtlest"),
    fontVariantNumeric: "slashed-zero",
    margin: 0
});

const pressVisualUpdateStyles = xcss({
    paddingBlock: "space.150",
    paddingInline: "space.200",
    maxWidth: "100%"
});

const tileDescriptionVisualUpdateStyles = css({
    font: token("font.body"),
    color: token("color.text.subtlest")
});

const tileTitleVisualUpdateStyles = css({
    font: token("font.body"),
    color: token("color.text")
});

const subtextVisualUpdateStyles = css({
    font: token("font.body"),
    color: token("color.text.subtle"),
    margin: 0
});

const titleVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});
